import React, { createContext, useContext, useEffect, useState } from 'react';
import { getBreakerPairs, unpairBreakerWithEgg, pairBreakerWithEgg } from 'utils/PointsAPI';
import { useUser } from '../hooks/useUser';

export type EggBreakerPair = {
  id: number;
  gribbleId: number;
  breakerId: number;
};

type EggBreakerPairContextType = {
  pairs: EggBreakerPair[];
  requestCompleted: boolean;
  pair: (userId: string, breakerId: number, gribbleId: number) => void;
  unpair: (userId: string, id: number) => void;
};

const EggBreakerPairContext = createContext<EggBreakerPairContextType>({
  pairs: [],
  requestCompleted: false,
  pair: () => {},
  unpair: () => {},
});

const EggBreakerPairProvider = ({ children }: { children: React.ReactNode }) => {
  const [pairs, setPairs] = useState<EggBreakerPair[]>([]);
  const [requestCompleted, setRequestCompleted] = useState(false);
  const { account } = useUser();

  useEffect(() => {
    const getPairs = async () => {
      setPairs(await getBreakerPairs(account.dusktech?.userId || ''));
      setRequestCompleted(true);
    };

    getPairs();
  }, [account.dusktech?.userId]);

  const pair = async (userId: string, breakerId: number, gribbleId: number) => {
    const newPair = await pairBreakerWithEgg(userId, breakerId, gribbleId);

    setPairs((currentPairs) => {
      return [
        ...currentPairs,
        {
          id: parseInt(newPair.pairId, 10),
          breakerId: newPair.breakerTokenId,
          gribbleId: newPair.gribbleTokenId,
        },
      ];
    });
  };

  const unpair = async (userId: string, id: number) => {
    await unpairBreakerWithEgg(userId, id);

    setPairs((currentPairs) => currentPairs.filter(({ id: pairId }) => pairId !== id));
  };

  return (
    <EggBreakerPairContext.Provider
      value={{
        pairs,
        requestCompleted,
        pair,
        unpair,
      }}
    >
      {children}
    </EggBreakerPairContext.Provider>
  );
};

const useEggBreakerPairs = () => {
  return useContext(EggBreakerPairContext);
};

export { useEggBreakerPairs, EggBreakerPairProvider };
